import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";

import { isValid } from "date-fns";

import { useDedicatedSlotEnforcement } from "hooks/use-dedicated-slot-enforcement.hook";
import type { AppointmentBlock } from "services/types/events.type";
import { getDateTime } from "utils/common";
import {
  type BookingWindow,
  type DedicatedSlot,
  blockToDedicatedSlot,
  findOverlappingSlots,
  findReservingSlots,
  legacyBlockIntersects,
  reservedTypeNames,
  slotAcceptsType
} from "utils/dedicated-slots.util";

import useAppointments from "../hooks/use-appointments.hook";

const ReservedAppointmentTypesContext = createContext<any>(null);

type ReservedAppointmentTypesProviderProps = {
  children?: React.ReactNode;
};

// Kept under its old name for existing importers; the mapping lives in the pure module.
export const toDedicatedSlot = blockToDedicatedSlot;

export function ReservedAppointmentTypesProvider({
  children
}: ReservedAppointmentTypesProviderProps) {
  // Unfiltered: the grid's type filter hides a slot whose first type is not selected, but the
  // backend still refuses a booking inside it (ENG-2105 review).
  const { allAppointmentBlocks } = useAppointments();
  const { isEnforced } = useDedicatedSlotEnforcement();

  const [appointmentStartDate, setAppointmentStartDate] = useState<
    number | undefined
  >();
  const [appointmentEndDate, setAppointmentEndDate] = useState<
    number | undefined
  >();

  const [appointmentTypeId, setAppointmentTypeId] = useState<
    number | undefined
  >();

  const [staffMemberId, setStaffMemberId] = useState<number | undefined>();
  const [clinicId, setClinicId] = useState<number | undefined>();

  const setupAppointmentTypes = useCallback(
    (
      date: Date | string,
      startTime: Date,
      endTime: Date,
      appointmentTypeIdParam: number,
      staffMemberId: number,
      clinicIdParam?: number
    ) => {
      setAppointmentTypeId(appointmentTypeIdParam);
      const appointmentDate = isValid(new Date(date.toString()))
        ? new Date(date.toString())
        : null;

      if (
        appointmentDate &&
        isValid(appointmentDate) &&
        isValid(startTime) &&
        isValid(endTime)
      ) {
        const newStartTime = getDateTime(appointmentDate, startTime);
        const newEndTime = getDateTime(appointmentDate, endTime);

        setAppointmentStartDate(newStartTime.getTime());
        setAppointmentEndDate(newEndTime.getTime());
      }
      setStaffMemberId(staffMemberId);
      setClinicId(clinicIdParam);
    },
    []
  );

  const blocks: AppointmentBlock[] = useMemo(
    () =>
      (allAppointmentBlocks ?? []).filter(
        (appointment: any) => appointment.type === "Block"
      ),
    [allAppointmentBlocks]
  );

  const bookingWindow: BookingWindow | undefined = useMemo(
    () =>
      !appointmentStartDate || !appointmentEndDate || !appointmentTypeId
        ? undefined
        : {
            staffMemberId,
            clinicId,
            start: new Date(appointmentStartDate),
            end: new Date(appointmentEndDate),
            appointmentTypeId
          },
    [
      appointmentStartDate,
      appointmentEndDate,
      appointmentTypeId,
      staffMemberId,
      clinicId
    ]
  );

  const result = useMemo(() => {
    if (!bookingWindow) {
      return {
        isReserved: false,
        intersectingBlocks: [],
        reservingTypeNames: [],
        bookingStart: undefined,
        bookingEnd: undefined,
        setupAppointmentTypes
      };
    }
    const slots = blocks.map(toDedicatedSlot);
    const blockFor = (slot: DedicatedSlot) => blocks[slots.indexOf(slot)];

    // Enforcement on: true interval overlap, clinic-wide slots count (ENG-2105). Off: today's
    // overlap test, unchanged; only the multi-type fix (any of the slot's types fits) applies.
    const overlapping = isEnforced
      ? findOverlappingSlots(slots, bookingWindow)
      : slots.filter((slot) => legacyBlockIntersects(slot, bookingWindow));
    const reserving = isEnforced
      ? findReservingSlots(overlapping, bookingWindow)
      : overlapping.filter(
          (slot) =>
            !slotAcceptsType(
              slot.appointmentTypes,
              bookingWindow.appointmentTypeId
            )
        );

    return {
      isReserved: reserving.length > 0,
      intersectingBlocks: overlapping.map(blockFor),
      reservingTypeNames: reservedTypeNames(reserving),
      bookingStart: bookingWindow.start,
      bookingEnd: bookingWindow.end,
      setupAppointmentTypes
    };
  }, [blocks, bookingWindow, isEnforced, setupAppointmentTypes]);

  return (
    <ReservedAppointmentTypesContext.Provider value={result}>
      {children}
    </ReservedAppointmentTypesContext.Provider>
  );
}

export default function useReservedAppointmentTypes() {
  const context = useContext(ReservedAppointmentTypesContext);
  if (!context) {
    throw new Error(
      `useReservedAppointmentTypes must be used within a ReservedAppointmentTypesProvider`
    );
  }

  const {
    isReserved,
    intersectingBlocks,
    reservingTypeNames = [],
    bookingStart,
    bookingEnd,
    setupAppointmentTypes
  } = context;
  return {
    isReserved,
    intersectingBlocks,
    reservingTypeNames: reservingTypeNames as string[],
    bookingStart: bookingStart as Date | undefined,
    bookingEnd: bookingEnd as Date | undefined,
    setupAppointmentTypes
  };
}
